import { base64UrlDecode, kernelCanonicalJson } from "./kernel-encoding.js";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/kernel/kernel-internal.ts
/**
* Helpers that the kernel contract modules share (kernel apps, WS-1a). Internal to
* `@zion/sdk-contracts`: the root barrel and the `testing` barrel do not re-export this module, and
* the package `exports` map gives no path to it. A consumer imports the public names of
* `kernel-encoding.ts`, `kernel-rights.ts`, `release-statement.ts` and `rights-certificate.ts`.
*
* Browser-safe: no Node builtin and no Node global. The ECDSA checks use the WebCrypto global
* `crypto.subtle`.
*/
/** A lower-case UUID of any version. */
const LOWER_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** A key id: 1 to 128 base64url characters. The certificate kid pattern and the signature kid share it. */
const KERNEL_KID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
/** True when `values` is in strict ascending UTF-16 code unit order: sorted, with no duplicate. Pure. */
function isStrictlyAscending(values) {
	for (let i = 1; i < values.length; i += 1) if (values[i] <= values[i - 1]) return false;
	return true;
}
/** The UTF-8 length of the canonical JSON text of `value`. Throws `KernelEncodingError` as canonical JSON does. */
function canonicalByteLength(value) {
	return utf8ToBytes(kernelCanonicalJson(value)).length;
}
/** The bytes of `text` as canonical base64url, or `null` when the text is not. Never throws. */
function base64UrlBytesOrNull(text) {
	try {
		return base64UrlDecode(text);
	} catch {
		return null;
	}
}
/**
* The WebCrypto verify key of a public P-256 ES256 JWK, or `null`. `null` answers a key that is not
* `kty: 'EC'`, `crv: 'P-256'`, `alg: 'ES256'` with both coordinates, and a key whose point
* WebCrypto refuses. Never rejects.
*/
async function importEs256VerifyKey(key) {
	if (key.kty !== "EC" || key.crv !== "P-256" || key.alg !== "ES256" || key.y === void 0) return null;
	try {
		return await crypto.subtle.importKey("jwk", {
			kty: "EC",
			crv: "P-256",
			x: key.x,
			y: key.y,
			ext: true
		}, {
			name: "ECDSA",
			namedCurve: "P-256"
		}, false, ["verify"]);
	} catch {
		return null;
	}
}
/**
* True when `signature`, the raw 64-byte `r || s` form, is a valid ECDSA P-256 SHA-256 signature
* of `key` over `data`. Never rejects for a wrong signature.
*/
async function verifyEs256(key, signature, data) {
	return crypto.subtle.verify({
		name: "ECDSA",
		hash: "SHA-256"
	}, key, new Uint8Array(signature), new Uint8Array(data));
}
//#endregion
export { KERNEL_KID_PATTERN, LOWER_UUID_PATTERN, base64UrlBytesOrNull, canonicalByteLength, importEs256VerifyKey, isStrictlyAscending, verifyEs256 };

//# sourceMappingURL=kernel-internal.js.map